/**
 * Firebase Auth compatibility layer using Supabase Auth.
 * Drop-in replacement for 'firebase/auth' imports.
 */
import { createClient } from '@supabase/supabase-js';
import { supabase } from './supabase';

const _SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://tuvupfivwfktvqcuvdlk.supabase.co';
const _SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

let _currentUser = null;

supabase.auth.onAuthStateChange((_event, session) => {
  _currentUser = session ? _mapUser(session.user) : null;
});

function _mapUser(supabaseUser) {
  if (!supabaseUser) return null;
  // Usa o Firebase UID original (guardado na migração) para que todas as queries
  // de dados continuem funcionando com os IDs já existentes no banco.
  const uid = supabaseUser.user_metadata?.firebaseUid || supabaseUser.id;
  return {
    uid,
    email: supabaseUser.email,
    displayName: supabaseUser.user_metadata?.displayName || supabaseUser.email,
    // Aluno externo do INEP: marca gravada pelo servidor ao criar a conta. O banco já
    // bloqueia o resto (docs/sql/2026-10-10-rls-so-inep.sql); aqui só some com as telas.
    soInep: supabaseUser.app_metadata?.so_inep === true,
    _supabaseUser: supabaseUser,
    _supabaseId: supabaseUser.id,
  };
}

/** Equivalent to Firebase's auth object / getAuth() */
export const auth = {
  get currentUser() {
    return _currentUser;
  },
  // Firebase v8 compat: auth.onAuthStateChanged(callback)
  onAuthStateChanged(callback) {
    supabase.auth.getSession().then(({ data: { session } }) => {
      _currentUser = session ? _mapUser(session.user) : null;
      callback(_currentUser);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      _currentUser = session ? _mapUser(session.user) : null;
      callback(_currentUser);
    });
    return () => subscription.unsubscribe();
  },
};

export function getAuth() {
  return auth;
}

export function onAuthStateChanged(_auth, callback) {
  let initialized = false;

  // Call immediately with current session
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (!initialized) {
      _currentUser = session ? _mapUser(session.user) : null;
      callback(_currentUser);
      initialized = true;
    }
  });

  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    _currentUser = session ? _mapUser(session.user) : null;
    if (initialized || _event !== 'INITIAL_SESSION') {
      callback(_currentUser);
      initialized = true;
    }
  });

  return () => subscription.unsubscribe();
}

export async function signInWithEmailAndPassword(_auth, email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    const mapped = _mapAuthError(error);
    throw mapped;
  }
  _currentUser = _mapUser(data.user);
  return { user: _currentUser };
}

export async function signOut(_auth) {
  // histórico de acessos: registra a saída antes de encerrar (não espera mais que 1,5 s)
  try {
    const { registrarAcesso } = await import('./utils/registrarAcesso');
    await Promise.race([registrarAcesso({ tipo: 'logout', caminho: window.location.pathname }), new Promise(r => setTimeout(r, 1500))]);
  } catch (_) { /* segue */ }
  localStorage.removeItem('qh_session_token');
  // scope 'local': encerra só esta sessão. O padrão do Supabase ('global') derruba a pessoa
  // em todos os aparelhos — e o logout por inatividade de uma aba esquecida em outro
  // computador encerrava a sessão de quem estava usando o sistema.
  const { error } = await supabase.auth.signOut({ scope: 'local' });
  if (error) throw error;
  _currentUser = null;
}

export async function sendPasswordResetEmail(_auth, email) {
  // O link do e-mail é montado pelo modelo public/email-templates/recuperar-senha.html
  // (→ /nova-senha?token_hash=...). GoTrue responde 200 mesmo para e-mail não cadastrado.
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/nova-senha`,
  });
  if (error) {
    if (error.status === 429) {
      const err = new Error(error.message);
      err.code = 'auth/too-many-requests';
      throw err;
    }
    throw error;
  }
}

// Troca a senha do próprio usuário pelo servidor (api/trocar-senha): confere a força,
// recusa senha "parecida" com as anteriores e registra a data da troca.
export async function trocarSenha(novaSenha, senhaAtual) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada. Entre de novo.');
  const resp = await fetch('/api/trocar-senha', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ novaSenha, senhaAtual }),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(body.error || 'Não foi possível salvar a senha.');
    err.code = body.codigo || 'auth/unknown';
    throw err;
  }
}

export async function updatePassword(_user, newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export async function reauthenticateWithCredential(_user, credential) {
  const { error } = await supabase.auth.signInWithPassword({
    email: credential.email,
    password: credential.password,
  });
  if (error) {
    const mapped = _mapAuthError(error);
    throw mapped;
  }
}

// "Primeiro acesso": envia um código de 6 dígitos para quem já tem conta (modelo
// public/email-templates/codigo-acesso.html). Não cria conta nova. A resposta é a
// mesma para e-mail cadastrado ou não, para ninguém descobrir quem tem acesso.
export async function sendFirstAccessCode(email) {
  const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
  if (error && error.status === 429) {
    const err = new Error(error.message);
    err.code = 'auth/too-many-requests';
    throw err;
  }
  if (error && !/signups not allowed|not found|user not found/i.test(error.message || '')) throw error;
}

// Cria o usuário já confirmado via API admin (rota /api/create-user, secret key só no servidor).
// A senha é gerada no servidor e ninguém a vê — a pessoa cria a própria em "Primeiro acesso".
// Retorna null se a rota não existir no ambiente atual (ex.: localhost sem o server.js),
// para então cair no fallback de signUp direto.
async function _tryCreateUserViaAdminApi(email) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const chamar = (token) => fetch('/api/create-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ email }),
    });
    let resp = await chamar(session.access_token);
    if (!resp.headers.get('content-type')?.includes('application/json')) return null;

    // Sessão recusada pelo servidor (ex.: encerrada após troca de senha em outro lugar):
    // renova o login e tenta mais uma vez antes de desistir.
    if (resp.status === 401) {
      const { data: ref } = await supabase.auth.refreshSession();
      if (ref?.session?.access_token) {
        resp = await chamar(ref.session.access_token);
      } else {
        // Sessão encerrada de vez (ex.: senha trocada pelo código em outro lugar) — sai e volta ao login
        setTimeout(() => { signOut().finally(() => { window.location.href = '/login'; }); }, 2500);
        const err = new Error('Sua sessão foi encerrada (a senha foi trocada em outro lugar). Você será levado ao login para entrar de novo.');
        err.code = 'auth/requires-recent-login';
        return { error: err };
      }
    }

    const body = await resp.json();
    if (!resp.ok) {
      const err = new Error(resp.status === 401
        ? `Sua sessão expirou. Clique em Sair, entre de novo e tente outra vez. [${body.error || ''}]`
        : (body.error || 'Erro ao criar usuário.'));
      err.code = resp.status === 409 ? 'auth/email-already-in-use' : 'auth/unknown';
      return { error: err };
    }
    return { user: _mapUser({ id: body.user.id, email: body.user.email, user_metadata: {} }) };
  } catch {
    return null;
  }
}

// A senha recebida só é usada no fallback de localhost; em produção quem define é o servidor.
export async function createUserWithEmailAndPassword(_auth, email, password) {
  const viaAdminApi = await _tryCreateUserViaAdminApi(email);
  if (viaAdminApi) {
    if (viaAdminApi.error) throw viaAdminApi.error;
    return { user: viaAdminApi.user };
  }

  // Fallback: signUp direto pelo client (usado em localhost, sem Vercel functions).
  // Usa cliente temporário sem persistência de sessão para não deslogar o admin atual
  // (equivalente ao Firebase secondaryApp pattern)
  const tempClient = createClient(_SUPABASE_URL, _SUPABASE_ANON_KEY, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
  const senhaFallback = password || Array.from(crypto.getRandomValues(new Uint8Array(24)), b => b.toString(16).padStart(2, '0')).join('');
  const { data, error } = await tempClient.auth.signUp({ email, password: senhaFallback });
  if (error) {
    const err = new Error(error.message);
    if (/already (been )?registered|already exists/i.test(error.message || '')) {
      err.code = 'auth/email-already-in-use';
    } else {
      err.code = 'auth/unknown';
    }
    throw err;
  }
  return { user: _mapUser(data.user) };
}

export const EmailAuthProvider = {
  credential(email, password) {
    return { email, password };
  },
};

function _mapAuthError(error) {
  const msg = error.message?.toLowerCase() || '';
  const err = new Error(error.message);
  if (msg.includes('invalid login credentials') || msg.includes('wrong password') || msg.includes('invalid password')) {
    err.code = 'auth/wrong-password';
  } else if (msg.includes('user not found') || msg.includes('no user')) {
    err.code = 'auth/user-not-found';
  } else if (msg.includes('recent login') || msg.includes('reauthenticate')) {
    err.code = 'auth/requires-recent-login';
  } else if (msg.includes('email not confirmed')) {
    err.code = 'auth/email-not-confirmed';
  } else if (msg.includes('banned')) {
    err.code = 'auth/user-disabled';
  } else if (msg.includes('email logins are disabled')) {
    err.code = 'auth/email-logins-disabled';
  } else {
    err.code = 'auth/unknown';
  }
  return err;
}
